import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, type PanInfo } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Image as ImageIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

export type CoverflowItem = {
  id: string;
  thumbnailUrl?: string;
  videoUrl?: string;
  title?: string;
  subtitle?: string;
  model?: string;
};

type Props = {
  items: CoverflowItem[];
  onOpen?: (item: CoverflowItem) => void;
  autoplay?: boolean;
  className?: string;
};

// How far to the sides we still render cards (keeps the DOM small + the stack readable).
const WINDOW = 3;
// Apple-ish spring — no linear snaps.
const SPRING = { type: 'spring' as const, stiffness: 260, damping: 30, mass: 0.9 };

// Per-offset 3D transform. offset = index - active.
//  center (0):      flat, front, full scale, elevated.
//  neighbours (±1): rotate on Y toward the centre, pushed back on Z, scaled + faded.
// This is the inverted-perspective coverflow: side cards face inward and recede.
function transformFor(offset: number) {
  const dir = Math.sign(offset);
  const abs = Math.abs(offset);
  const clamped = Math.min(abs, WINDOW);
  return {
    x: offset * 190,                         // lateral fan-out
    rotateY: -dir * Math.min(abs, 2) * 45,   // ±45deg, capped so far cards don't over-rotate
    translateZ: -clamped * 120,              // recede into the scene
    scale: Math.max(0.72, 1 - abs * 0.16),
    opacity: abs > WINDOW ? 0 : Math.max(0.18, 1 - abs * 0.32),
    zIndex: 100 - clamped,
  };
}

export function CoverflowGallery({ items, onOpen, autoplay = false, className }: Props) {
  const [active, setActive] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const count = items.length;

  // modular wrap — last→first, first→last. Keeps the spring smooth (no clamp jump).
  const wrap = useCallback((i: number) => (count ? ((i % count) + count) % count : 0), [count]);
  const go = useCallback((i: number) => setActive(() => wrap(i)), [wrap]);
  const next = useCallback(() => setActive((a) => wrap(a + 1)), [wrap]);
  const prev = useCallback(() => setActive((a) => wrap(a - 1)), [wrap]);

  // keep active in range if the list shrinks
  useEffect(() => { setActive((a) => Math.max(0, Math.min(count - 1, a))); }, [count]);

  // keyboard arrows when the gallery (or a child) has focus
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen?.(items[active]); }
  };

  // autoplay — pauses on hover
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    if (!autoplay || hovered || count < 2) return;
    const t = setInterval(() => setActive((a) => (a + 1) % count), 3200);
    return () => clearInterval(t);
  }, [autoplay, hovered, count]);

  // drag-to-advance: throw distance / velocity decides how many cards to move.
  const onDragEnd = (_e: any, info: PanInfo) => {
    const throwX = info.offset.x + info.velocity.x * 0.18;
    const steps = Math.round(-throwX / 190);
    if (steps !== 0) setActive((a) => wrap(a + steps));
  };

  // The drag surface sits above every card, so card <button> clicks never land.
  // Route taps here: a tap on the centred card opens it; a tap on a side card
  // centres it. `moved` distinguishes a real drag (which must not trigger a tap).
  const moved = useRef(false);
  const onSurfaceDrag = (_e: any, info: PanInfo) => {
    if (Math.abs(info.offset.x) > 6) moved.current = true;
  };
  const onSurfaceClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (moved.current) { moved.current = false; return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const dx = e.clientX - (rect.left + rect.width / 2);
    const offset = Math.max(-WINDOW, Math.min(WINDOW, Math.round(dx / 190)));
    if (offset === 0) onOpen?.(items[active]);
    else setActive((a) => wrap(a + offset));
  };

  if (!count) return null;

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={cn(
        'relative w-full select-none outline-none',
        'rounded-3xl bg-gradient-to-b from-white/[0.03] to-transparent',
        className
      )}
      aria-roledescription="carousel"
    >
      {/* 3D stage */}
      <div
        className="relative mx-auto flex items-center justify-center"
        style={{ perspective: 1400, height: 400 }}
      >
        {/* drag surface — captures the swipe across the whole stage */}
        <motion.div
          className="absolute inset-0 z-[200] cursor-grab active:cursor-grabbing"
          drag={count > 1 ? 'x' : false}
          dragElastic={0.16}
          dragConstraints={{ left: 0, right: 0 }}
          dragSnapToOrigin
          onDrag={onSurfaceDrag}
          onDragEnd={onDragEnd}
          onClick={onSurfaceClick}
        />

        <div className="relative h-full w-full" style={{ transformStyle: 'preserve-3d' }}>
          {items.map((item, i) => {
            // circular offset so the stack wraps: item 0 can sit just right of the last card.
            let offset = i - active;
            if (count > 1) {
              if (offset > count / 2) offset -= count;
              else if (offset < -count / 2) offset += count;
            }
            if (Math.abs(offset) > WINDOW) return null;
            const t = transformFor(offset);
            const isCenter = offset === 0;
            return (
              <motion.div
                key={item.id}
                className="absolute left-1/2 top-1/2"
                style={{ zIndex: t.zIndex, transformStyle: 'preserve-3d' }}
                initial={false}
                animate={{
                  x: t.x - 120,          // 120 = half card width, centre the card on the axis
                  y: -190,               // half card height
                  rotateY: t.rotateY,
                  z: t.translateZ,
                  scale: t.scale,
                  opacity: t.opacity,
                }}
                transition={SPRING}
              >
                <button
                  type="button"
                  onClick={() => (isCenter ? onOpen?.(item) : go(i))}
                  tabIndex={isCenter ? 0 : -1}
                  className={cn(
                    'group relative block h-[380px] w-[240px] overflow-hidden rounded-2xl bg-neutral-900 text-left',
                    'ring-1 transition-shadow duration-300',
                    isCenter
                      ? 'ring-violet-400/60 shadow-[0_30px_80px_-20px_rgba(139,92,246,0.55)]'
                      : 'ring-white/10 shadow-2xl'
                  )}
                >
                  {item.thumbnailUrl ? (
                    <img
                      src={item.thumbnailUrl}
                      alt={item.title || ''}
                      draggable={false}
                      loading="eager"
                      decoding="async"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-violet-500/15 to-fuchsia-500/10 p-5 text-center">
                      <ImageIcon className="h-8 w-8 text-violet-300/70" />
                      <span className="text-sm font-medium text-gray-200 line-clamp-3">
                        {item.title || 'Untitled'}
                      </span>
                    </div>
                  )}

                  {/* dim non-centre cards so the focus reads clearly */}
                  {!isCenter && <div className="absolute inset-0 bg-black/40" />}

                  {/* play affordance + caption only on the centred card */}
                  {isCenter && (
                    <>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-black/45 backdrop-blur-sm ring-1 ring-white/25 transition group-hover:scale-110 group-hover:bg-black/60">
                          <Play className="ml-0.5 h-7 w-7 fill-white text-white" />
                        </span>
                      </div>
                      {(item.title || item.model || item.subtitle) && (
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-4 pt-10">
                          {/* primary label = the prompt/title */}
                          <div className="line-clamp-2 text-sm font-semibold text-white">
                            {item.title || 'Untitled'}
                          </div>
                          {(item.model || item.subtitle) && (
                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                              {item.model && (
                                <span className="inline-flex items-center rounded-full bg-violet-500/25 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-violet-200 ring-1 ring-violet-400/30">
                                  {item.model}
                                </span>
                              )}
                              {item.subtitle && (
                                <span className="truncate text-[11px] text-gray-300">{item.subtitle}</span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* arrows */}
      {count > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Previous"
            className="absolute left-2 top-1/2 z-[210] -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white ring-1 ring-white/15 backdrop-blur transition hover:bg-black/70 sm:left-4"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next"
            className="absolute right-2 top-1/2 z-[210] -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white ring-1 ring-white/15 backdrop-blur transition hover:bg-black/70 sm:right-4"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* position indicator — {active+1} / {total} */}
      {count > 1 && (
        <div className="pointer-events-none absolute left-1/2 top-3 z-[210] -translate-x-1/2 rounded-full bg-black/55 px-3 py-1 text-xs font-medium tabular-nums text-violet-200 ring-1 ring-violet-400/30 backdrop-blur">
          {active + 1} / {count}
        </div>
      )}

      {/* pagination dots */}
      {count > 1 && count <= 24 && (
        <div className="mt-2 flex items-center justify-center gap-1.5">
          <AnimatePresence initial={false}>
            {items.map((it, i) => (
              <button
                key={it.id}
                type="button"
                onClick={() => go(i)}
                aria-label={`Go to item ${i + 1}`}
                className="p-1"
              >
                <motion.span
                  className="block rounded-full bg-white"
                  animate={{
                    width: i === active ? 20 : 6,
                    opacity: i === active ? 1 : 0.35,
                  }}
                  transition={SPRING}
                  style={{ height: 6 }}
                />
              </button>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

export default CoverflowGallery;
